// src/services/categoryService.ts
import api from './api';

export interface Category {
  id: number;
  name: string;
  description?: string;
}

export interface CategoryCreate {
  name: string;
  description?: string;
}

export interface CategoriesResponse {
  categories: Category[];
  total: number;
}

export interface CategoryStats {
  category_id: number;
  category_name: string;
  products_count: number;
}

class CategoryService {
  private cache: Category[] | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly FALLBACK_CATEGORIES: Category[] = [
    { id: 1, name: 'Céréales', description: 'Maïs, mil, sorgho, riz et autres céréales' },
    { id: 2, name: 'Légumes', description: 'Tomates, oignons, carottes et légumes de saison' },
    { id: 3, name: 'Fruits', description: 'Ananas, mangues, bananes et fruits tropicaux' },
    { id: 4, name: 'Légumineuses', description: 'Haricots, niébé, arachides et légumineuses' },
    { id: 5, name: 'Tubercules', description: 'Ignames, manioc, patates douces et tubercules' },
    { id: 6, name: 'Épices et aromates', description: 'Piment, gingembre, ail et herbes aromatiques' }
  ];

  /**
   * Récupérer toutes les catégories avec cache intelligent
   */
  async getCategories(forceRefresh = false): Promise<Category[]> {
    // Vérifier le cache
    if (!forceRefresh && this.cache && Date.now() < this.cacheExpiry) {
      console.log('📦 Categories loaded from cache');
      return this.cache;
    }

    try {
      console.log('🌐 Fetching categories from API...');
      const response = await api.get('/categories/');
      
      // Valider la réponse
      if (!Array.isArray(response.data)) {
        throw new Error('Format de réponse invalide');
      }

      // Mettre à jour le cache
      this.cache = response.data;
      this.cacheExpiry = Date.now() + this.CACHE_DURATION;
      
      console.log(`✅ ${this.cache.length} categories loaded successfully`);
      return this.cache;

    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      
      // Fallback intelligent
      if (this.cache) {
        console.log('🔄 Using expired cache as fallback');
        return this.cache;
      } else {
        console.log('🆘 Using hardcoded fallback categories');
        return this.getFallbackCategories();
      }
    }
  }

  /**
   * Récupérer une catégorie spécifique
   */
  async getCategory(id: number): Promise<Category | null> {
    try {
      // Essayer d'abord le cache
      const cachedCategories = await this.getCategories();
      const fromCache = cachedCategories.find(cat => cat.id === id);
      if (fromCache) {
        return fromCache;
      }

      // Sinon, appel API direct
      const response = await api.get(`/categories/${id}`);
      return response.data;

    } catch (error) {
      console.error(`Error fetching category ${id}:`, error);
      return null;
    }
  }

  /**
   * Rechercher des catégories
   */
  async searchCategories(query: string): Promise<Category[]> {
    try {
      const response = await api.get('/categories/', {
        params: { search: query, limit: 50 }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching categories:', error);
      
      // Fallback: recherche locale dans le cache
      const categories = await this.getCategories();
      return categories.filter(cat => 
        cat.name.toLowerCase().includes(query.toLowerCase()) ||
        (cat.description && cat.description.toLowerCase().includes(query.toLowerCase()))
      );
    }
  }

  /**
   * Obtenir les statistiques d'une catégorie
   */
  async getCategoryStats(id: number): Promise<CategoryStats | null> {
    try {
      const response = await api.get(`/categories/${id}/products-count`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching stats for category ${id}:`, error);
      return null;
    }
  }

  /**
   * Créer une nouvelle catégorie (admin uniquement)
   */
  async createCategory(categoryData: CategoryCreate): Promise<Category> {
    try {
      const response = await api.post('/categories/', categoryData);
      
      // Invalider le cache
      this.clearCache();
      
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une catégorie (admin uniquement)
   */
  async updateCategory(id: number, categoryData: CategoryCreate): Promise<Category> {
    try {
      const response = await api.put(`/categories/${id}`, categoryData);
      
      // Invalider le cache
      this.clearCache();
      
      return response.data;
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error;
    }
  }

  /**
   * Supprimer une catégorie (admin uniquement)
   */
  async deleteCategory(id: number): Promise<void> {
    try {
      await api.delete(`/categories/${id}`);
      
      // Invalider le cache
      this.clearCache();
      
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtenir les catégories de fallback
   */
  private getFallbackCategories(): Category[] {
    return [...this.FALLBACK_CATEGORIES];
  }

  /**
   * Vider le cache
   */
  clearCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
    console.log('🗑️ Categories cache cleared');
  }

  /**
   * Vérifier si le cache est valide
   */
  isCacheValid(): boolean {
    return this.cache !== null && Date.now() < this.cacheExpiry;
  }

  /**
   * Obtenir les informations du cache
   */
  getCacheInfo(): { 
    hasCache: boolean; 
    isValid: boolean; 
    expiresIn: number;
    categoriesCount: number;
  } {
    const now = Date.now();
    return {
      hasCache: this.cache !== null,
      isValid: this.isCacheValid(),
      expiresIn: Math.max(0, this.cacheExpiry - now),
      categoriesCount: this.cache?.length || 0
    };
  }

  /**
   * Précharger les catégories en arrière-plan
   */
  async preloadCategories(): Promise<void> {
    try {
      await this.getCategories();
      console.log('🔄 Categories preloaded successfully');
    } catch (error) {
      console.error('Failed to preload categories:', error);
    }
  }

  /**
   * Valider une catégorie avant utilisation
   */
  validateCategory(category: any): category is Category {
    return (
      typeof category === 'object' &&
      category !== null &&
      typeof category.id === 'number' &&
      typeof category.name === 'string' &&
      category.name.length > 0
    );
  }

  /**
   * Formater le nom d'une catégorie pour l'affichage
   */
  formatCategoryName(category: Category): string {
    return category.name.charAt(0).toUpperCase() + category.name.slice(1).toLowerCase();
  }

  /**
   * Obtenir une description courte d'une catégorie
   */
  getShortDescription(category: Category, maxLength = 50): string {
    if (!category.description) return '';
    
    if (category.description.length <= maxLength) {
      return category.description;
    }
    
    return category.description.substring(0, maxLength).trim() + '...';
  }
}

// Exporter une instance singleton
export default new CategoryService();